import React, { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import AnnotationOverlay from "./AnnotationOverlay";
import { fetchAsBlobUrl } from "@/lib/uploads";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";

// Configure pdfjs worker from the same npm build to keep version aligned
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

/**
 * PDF renderer with rectangle annotation overlay per page.
 * Props:
 *  - fileId
 *  - annotations: filtered to this file already
 *  - canDraw
 *  - onCreate({page, x, y, w, h, remark})
 *  - onResolve(id)
 */
export default function PdfAnnotator({ fileId, annotations = [], canDraw, onCreate, onResolve }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(1.1);
  const [err, setErr] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    let live = true;
    let url = null;
    setBlobUrl(null); setNumPages(0); setPage(1); setErr("");
    (async () => {
      try {
        url = await fetchAsBlobUrl(fileId);
        if (live) setBlobUrl(url);
      } catch (e) {
        if (live) setErr("Failed to load PDF");
      }
    })();
    return () => {
      live = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [fileId]);

  const file = useMemo(() => (blobUrl ? { url: blobUrl } : null), [blobUrl]);

  return (
    <div className="w-full flex flex-col" data-testid="pdf-annotator">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#1a1a1a] bg-[#080808]">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="iz-btn-secondary p-1.5 disabled:opacity-30"
            data-testid="pdf-prev"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-400">
            Page {page} / {numPages || "…"}
          </p>
          <button
            onClick={() => setPage((p) => Math.min(numPages || p, p + 1))}
            disabled={!numPages || page >= numPages}
            className="iz-btn-secondary p-1.5 disabled:opacity-30"
            data-testid="pdf-next"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setScale((s) => Math.max(0.5, s - 0.15))} className="iz-btn-secondary p-1.5">
            <ZoomOut className="w-3.5 h-3.5" />
          </button>
          <p className="text-[10px] font-mono text-gray-500">{Math.round(scale * 100)}%</p>
          <button onClick={() => setScale((s) => Math.min(2.5, s + 0.15))} className="iz-btn-secondary p-1.5">
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 flex justify-center bg-[#050505]">
        {err && <p className="text-sm text-red-400 py-10">{err}</p>}
        {!err && !blobUrl && <p className="text-xs font-mono text-gray-600 py-10">Loading…</p>}
        {file && (
          <div ref={containerRef} className="relative inline-block" style={{ lineHeight: 0 }}>
            <Document
              file={file}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              onLoadError={() => setErr("PDF failed to render")}
              loading={<p className="text-xs font-mono text-gray-600 p-6">Loading…</p>}
            >
              <div className="relative">
                <Page
                  pageNumber={page}
                  scale={scale}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                />
                <AnnotationOverlay
                  annotations={annotations}
                  page={page}
                  canDraw={canDraw}
                  onCreate={(rect) => onCreate?.({ ...rect, page })}
                  onResolve={onResolve}
                />
              </div>
            </Document>
          </div>
        )}
      </div>
    </div>
  );
}
